import { z } from 'zod';

/**
 * Zod schemas are the FRONTEND-OWNED source of truth for API contracts while
 * the backend is in flight. Every response is validated at runtime so schema
 * drift surfaces loudly. When the backend ships OpenAPI, diff against these and
 * log changes in docs/api-reconciliation.md.
 */

export const ModifierOptionSchema = z.object({
  id: z.string(),
  name: z.string(),
  priceDelta: z.number().int(),
  sortOrder: z.number().int(),
});

export const ModifierGroupSchema = z.object({
  id: z.string(),
  name: z.string(),
  selectionType: z.enum(['SINGLE_REQUIRED', 'SINGLE_OPTIONAL', 'MULTI']),
  options: z.array(ModifierOptionSchema),
});

export const MenuItemVariantSchema = z.object({
  id: z.string(),
  name: z.string(),
  price: z.number().int().nullable(), // absolute override; null = inherits item basePrice
  effectivePrice: z.number().int(), // server-precomputed: price ?? basePrice — always use this
  sortOrder: z.number().int(),
  isAvailable: z.boolean(), // false = sold out ("HABIS"), still shown
});

export const MenuItemSchema = z.object({
  id: z.string().uuid(),
  categoryId: z.string(),
  name: z.string(),
  description: z.string().nullish(),
  basePrice: z.number().int(), // whole rupiah, no decimals
  imageUrl: z.string().url().nullish(),
  emoji: z.string().nullish(),
  tags: z.array(z.string()),
  sortOrder: z.number().int(),
  modifierGroups: z.array(ModifierGroupSchema),
  // Always present on the wire (backend DTO has no `omitempty` on this field).
  variants: z.array(MenuItemVariantSchema),
  // Frontend extension: the backend skeleton does not model availability yet.
  // Absent ⇒ available; only an explicit `false` marks an item sold out ("HABIS").
  // See docs/api-reconciliation.md.
  available: z.boolean().optional(),
});

export const CategorySchema = z.object({
  id: z.string(),
  name: z.string(),
  sortOrder: z.number().int(),
});

export const MenuResponseSchema = z.object({
  categories: z.array(CategorySchema),
  items: z.array(MenuItemSchema),
});

export const OrderTypeSchema = z.enum(['DINE_IN', 'TAKE_AWAY']);

export const OrderLineSchema = z.object({
  itemId: z.string().uuid(),
  variantId: z.string().uuid().nullish(), // required iff the item has variants
  qty: z.number().int().positive(),
  modifierOptionIds: z.array(z.string()),
});

export const CreateOrderRequestSchema = z.object({
  type: OrderTypeSchema,
  lines: z.array(OrderLineSchema).min(1),
});

export const OrderStatusSchema = z.enum([
  'DRAFT',
  'AWAITING_PAYMENT_AT_COUNTER',
  'PAID',
  'IN_PREPARATION',
  'READY',
  'COMPLETED',
  'EXPIRED',
  'CANCELLED',
  'REFUNDED',
]);

// Backend omits unset optional fields (Go `omitempty`) instead of sending
// null, and may emit RFC3339 timestamps with a UTC offset — see
// docs/api-reconciliation.md (2026-07-03).
export const OrderResponseSchema = z.object({
  id: z.string().uuid(),
  orderNo: z.string().nullish(),
  type: OrderTypeSchema,
  status: OrderStatusSchema,
  customerName: z.string().nullish(),
  subtotal: z.number().int(),
  serviceFee: z.number().int(),
  tax: z.number().int(),
  total: z.number().int(),
  expiresAt: z.string().datetime({ offset: true }).nullish(),
  createdAt: z.string().datetime({ offset: true }),
  paidAt: z.string().datetime({ offset: true }).nullish(),
});

export type MenuItem = z.infer<typeof MenuItemSchema>;
export type MenuItemVariant = z.infer<typeof MenuItemVariantSchema>;
export type ModifierOption = z.infer<typeof ModifierOptionSchema>;
export type ModifierGroup = z.infer<typeof ModifierGroupSchema>;
export type Category = z.infer<typeof CategorySchema>;
export type MenuResponse = z.infer<typeof MenuResponseSchema>;
export type OrderType = z.infer<typeof OrderTypeSchema>;
export type OrderStatus = z.infer<typeof OrderStatusSchema>;
export type OrderLine = z.infer<typeof OrderLineSchema>;
export type CreateOrderRequest = z.infer<typeof CreateOrderRequestSchema>;
export type OrderResponse = z.infer<typeof OrderResponseSchema>;
